import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ArrowLeft,
  Upload,
  Send,
  Search,
  Plus,
  Trash2,
  Edit,
  CheckCircle,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Users
} from "lucide-react";
import {
  fetchMailingList,
  fetchContactsWithMailCount,
  deleteContact,
  deleteContacts,
  formatAddress,
  STATUS_CONFIG
} from "../../services/MailingListService";
import ContactStatusBadge from "./ContactStatusBadge";
import ContactEditModal from "./ContactEditModal";
import ImportCSVModal from "./ImportCSVModal";
import CreateCampaignModal from "./CreateCampaignModal";
import ContactMailHistoryDropdown from "./ContactMailHistoryDropdown";

/**
 * MailingListDetail - Detail view with contacts table and actions
 */
const MailingListDetail = ({ listId, listName, onBack, userEmail, userMeta }) => {
  const [list, setList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [jumpToPage, setJumpToPage] = useState("");

  // Filter states
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Modal states
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showAddContact, setShowAddContact] = useState(false);

  // Selection states
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Load list details
  const loadList = useCallback(async () => {
    try {
      const response = await fetchMailingList(listId);
      setList(response);
    } catch (err) {
      setError(err.message);
    }
  }, [listId]);

  // Load contacts with pagination and mail counts
  const loadContacts = useCallback(async (pageNum = 1, search = "", city = "", status = "", size = pageSize) => {
    try {
      setIsLoadingContacts(true);
      // Use the new API that includes mail counts for each contact
      const response = await fetchContactsWithMailCount(listId, {
        page: pageNum,
        pageSize: size,
        search: search || undefined,
        city: city || undefined,
        status: status || undefined
      });
      setContacts(response.contacts || []);
      // Use total_count from response, or fall back to list's contact_count
      const total = response.total_count ?? response.total ?? 0;
      setTotalCount(total);
      setPage(pageNum);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoadingContacts(false);
    }
  }, [listId, pageSize]);

  // Track pending search to handle race conditions
  const searchRequestId = useRef(0);

  // Load list details on mount
  useEffect(() => {
    loadList();
  }, [loadList]);

  // Single effect for all contact loading (initial + search/filter changes)
  useEffect(() => {
    const currentRequestId = ++searchRequestId.current;

    const fetchData = async () => {
      // Only show full-page loading on initial mount
      if (contacts.length === 0 && !searchTerm && !cityFilter && !statusFilter) {
        setIsLoading(true);
      }

      console.log('[MailingListDetail] Loading contacts:', { searchTerm, cityFilter, statusFilter, requestId: currentRequestId });

      await loadContacts(1, searchTerm, cityFilter, statusFilter, pageSize);

      // Only update loading state if this is still the latest request
      if (currentRequestId === searchRequestId.current) {
        setIsLoading(false);
      }
    };

    // Debounce user input, but not initial load
    const delay = contacts.length === 0 ? 0 : 300;
    const timeout = setTimeout(fetchData, delay);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, cityFilter, statusFilter, pageSize, listId]);

  // Handle single contact delete
  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) {
      return;
    }

    try {
      await deleteContact(listId, contactId);
      setContacts((prev) => prev.filter((c) => c.id !== contactId));
      setTotalCount((prev) => prev - 1);
      loadList(); // Refresh stats
    } catch (err) {
      alert(err.message);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} contacts?`)) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteContacts(listId, Array.from(selectedIds));
      setContacts((prev) => prev.filter((c) => !selectedIds.has(c.id)));
      setTotalCount((prev) => prev - selectedIds.size);
      setSelectedIds(new Set());
      loadList(); // Refresh stats
    } catch (err) {
      alert(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    }
  };

  // Toggle single selection
  const toggleSelect = (contactId) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(contactId)) {
      newSet.delete(contactId);
    } else {
      newSet.add(contactId);
    }
    setSelectedIds(newSet);
  };

  // Pagination helpers - use list's contact_count as fallback for total
  const effectiveTotalCount = totalCount || list?.contact_count || 0;
  const totalPages = Math.max(1, Math.ceil(effectiveTotalCount / pageSize));
  const canGoPrev = page > 1;
  const canGoNext = page < totalPages;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error && !list) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
            Failed to load mailing list
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-4">{error}</p>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-slate-200 dark:border-slate-800 px-6 py-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Lists
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold dark:text-white text-slate-900">
              {list?.name || listName}
            </h2>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                {(list?.contact_count || 0).toLocaleString()} contacts
              </span>
              <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                <CheckCircle className="w-4 h-4" />
                {(list?.verified_count || 0).toLocaleString()} verified
              </span>
              <span className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                <Send className="w-4 h-4" />
                {(list?.sent_count || 0).toLocaleString()} sent
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Import CSV
            </button>
            <button
              onClick={() => setShowSendModal(true)}
              disabled={!list || (list.contact_count || 0) === 0}
              className="px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-lg font-medium flex items-center gap-2 transition-colors shadow-lg shadow-sky-900/20 disabled:shadow-none"
            >
              <Send className="w-4 h-4" />
              Create Campaign
            </button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search all contacts by name or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-80 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
            />
          </div>

          {/* City filter */}
          <input
            type="text"
            placeholder="Filter by city..."
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="px-3 py-2 w-40 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
          />

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </select>

          {/* Clear filters button */}
          {(searchTerm || cityFilter || statusFilter) && (
            <button
              onClick={() => {
                setSearchTerm("");
                setCityFilter("");
                setStatusFilter("");
              }}
              className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              Clear filters
            </button>
          )}

          {/* Bulk actions */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {selectedIds.size} selected
              </span>
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex items-center gap-1.5 transition-colors"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          )}
        </div>

        {/* Add contact button */}
        <button
          onClick={() => {
            setEditingContact(null);
            setShowAddContact(true);
          }}
          className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Contact
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {isLoadingContacts ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-6 h-6 text-sky-600 animate-spin" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
              {searchTerm || cityFilter || statusFilter ? "No contacts found" : "No contacts yet"}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {searchTerm || cityFilter || statusFilter
                ? "No contacts match the current filters. Try adjusting your search criteria."
                : "Import contacts from a CSV file or add them manually."}
            </p>
            {!(searchTerm || cityFilter || statusFilter) && (
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium inline-flex items-center gap-2 transition-colors"
              >
                <Upload className="w-4 h-4" />
                Import CSV
              </button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900/50 sticky top-0">
              <tr>
                <th className="w-12 px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === contacts.length && contacts.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-sky-600 focus:ring-sky-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Mail Sent
                </th>
                <th className="w-20 px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {contacts.map((contact) => (
                <tr
                  key={contact.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-colors"
                >
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(contact.id)}
                      onChange={() => toggleSelect(contact.id)}
                      className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-sky-600 focus:ring-sky-500"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium dark:text-white text-slate-900">
                      {contact.name || "—"}
                    </span>
                    {contact.email && (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {contact.email}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                    {formatAddress(contact) || "—"}
                  </td>
                  <td className="px-6 py-4">
                    <ContactStatusBadge status={contact.status} />
                  </td>
                  <td className="px-6 py-4">
                    <ContactMailHistoryDropdown
                      contactId={contact.id}
                      mailCount={contact.mail_count || 0}
                      deliveredCount={contact.delivered_count || 0}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingContact(contact);
                          setShowAddContact(true);
                        }}
                        className="p-1.5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteContact(contact.id)}
                        className="p-1.5 rounded text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination - always show if there are contacts */}
      {(totalCount > 0 || contacts.length > 0) && (
        <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {contacts.length > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, effectiveTotalCount)} of{" "}
              {effectiveTotalCount.toLocaleString()} contacts
            </p>

            {/* Page size selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 dark:text-slate-400">Per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  const newSize = parseInt(e.target.value, 10);
                  setPageSize(newSize);
                  loadContacts(1, searchTerm, cityFilter, statusFilter, newSize);
                }}
                className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* First page */}
            <button
              onClick={() => loadContacts(1, searchTerm, cityFilter, statusFilter)}
              disabled={page === 1 || isLoadingContacts}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="First page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>

            {/* Previous page */}
            <button
              onClick={() => loadContacts(page - 1, searchTerm, cityFilter, statusFilter)}
              disabled={!canGoPrev || isLoadingContacts}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page input */}
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-slate-500 dark:text-slate-400">Page</span>
              <input
                type="number"
                min={1}
                max={totalPages || 1}
                value={jumpToPage || page}
                onChange={(e) => setJumpToPage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const targetPage = parseInt(jumpToPage, 10);
                    if (targetPage >= 1 && targetPage <= totalPages) {
                      loadContacts(targetPage, searchTerm, cityFilter, statusFilter);
                    }
                    setJumpToPage("");
                  }
                }}
                onBlur={() => setJumpToPage("")}
                className="w-16 px-2 py-1 text-sm text-center border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
              <span className="text-sm text-slate-500 dark:text-slate-400">
                of {totalPages.toLocaleString()}
              </span>
            </div>

            {/* Next page */}
            <button
              onClick={() => loadContacts(page + 1, searchTerm, cityFilter, statusFilter)}
              disabled={!canGoNext || isLoadingContacts}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Last page */}
            <button
              onClick={() => loadContacts(totalPages, searchTerm, cityFilter, statusFilter)}
              disabled={page === totalPages || isLoadingContacts}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Last page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showImportModal && (
        <ImportCSVModal
          listId={listId}
          onClose={() => setShowImportModal(false)}
          onImported={() => {
            setShowImportModal(false);
            loadList();
            loadContacts(1, searchTerm, cityFilter, statusFilter, pageSize);
          }}
        />
      )}

      {showSendModal && list && (
        <CreateCampaignModal
          listId={listId}
          list={list}
          onClose={() => setShowSendModal(false)}
          onSent={() => {
            setShowSendModal(false);
            loadList();
            loadContacts(page, searchTerm, cityFilter, statusFilter, pageSize);
          }}
        />
      )}

      {showAddContact && (
        <ContactEditModal
          listId={listId}
          contact={editingContact}
          onClose={() => {
            setShowAddContact(false);
            setEditingContact(null);
          }}
          onSaved={(savedContact) => {
            if (editingContact) {
              setContacts((prev) =>
                prev.map((c) => (c.id === savedContact.id ? savedContact : c))
              );
            } else {
              setContacts((prev) => [savedContact, ...prev]);
              setTotalCount((prev) => prev + 1);
              loadList();
            }
            setShowAddContact(false);
            setEditingContact(null);
          }}
        />
      )}
    </div>
  );
};

export default MailingListDetail;
